import type {
  CreateAssetRequestDto,
  ExecuteAssetOcppCommandRequestDto,
  ExecuteAssetOcppCommandResponseDto,
  GetAssetOcppConfigurationResponseDto,
  GetAssetOcppSettingsResponseDto,
  SetAssetOcppCredentialsRequestDto,
  UpdateAssetRequestDto,
} from '@evimo/client/models';
import { ResponseError } from '@evimo/client/runtime';
import { z } from 'zod';
import type {
  OcppChangeConfigurationKey,
  OcppGetConfigurationKey,
  OcppResetType,
  OcppTriggerMessage,
} from '@evimo/types/constants/ocpp-charger-command';
import { EvseType } from '@evimo/types/enums/evse-type';
import { assetLivenessFieldsSchema } from '~/app/services/asset/asset-liveness.schema';
import { AssetListHttpError } from '~/app/services/asset/asset-list-error';
import {
  assetApi,
  assetRequestInit,
} from '~/app/services/asset/asset-api-instances';
import { parseClientJsonResponse } from '~/app/services/http/parse-client-json-response';

function coerceAssetListNumber(value: unknown): number {
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

const assetLookupSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const jsonRecordSchema = z.record(z.string(), z.unknown()).nullable();

const assetListItemSchema = z.object({
  id: z.string(),
  assetName: z.string(),
  serialNumber: z.preprocess(
    (value) => (typeof value === 'string' ? value : ''),
    z.string(),
  ),
  installedAt: z.string().nullable(),
  assetModel: assetLookupSchema.nullable(),
  assetType: assetLookupSchema,
  standard: z.enum([EvseType.AC, EvseType.DC, EvseType.AC_DC]).nullable(),
  landlord: assetLookupSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  preventivePlan: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  incidentsCount: z.number().int().nonnegative(),
  expectedDailyEarning: z.preprocess(coerceAssetListNumber, z.number()),
  calculatedDailyEarning: z.preprocess(coerceAssetListNumber, z.number()),
  createdAt: z.string(),
  updatedAt: z.string(),
  ...assetLivenessFieldsSchema,
});

const assetPaginationMetaSchema = z.object({
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

const listAssetsResponseSchema = z.object({
  data: z.array(assetListItemSchema),
  meta: assetPaginationMetaSchema,
});

const assetDetailCitySchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  postalCodes: z.array(z.string()),
});

const assetDetailSiteSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  postalCode: z
    .string()
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  city: assetDetailCitySchema.nullable(),
});

const assetDetailZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const assetDetailIncidentAffectedComponentSchema = z.object({
  id: z.string(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  componentClass: assetLookupSchema.nullable(),
});

const assetDetailIncidentSchema = z.object({
  id: z.string(),
  code: z.preprocess(
    (value) => (typeof value === 'string' ? value : ''),
    z.string(),
  ),
  title: z.string(),
  status: z.string(),
  severity: z.number().nullable(),
  description: z.string().nullable(),
  priority: z.string().nullable(),
  openedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  affectedComponents: z.array(assetDetailIncidentAffectedComponentSchema),
});

const assetDetailComponentControllerSchema = z.object({
  id: z.string(),
  name: z.string(),
  ocppId: z.string().nullable(),
});

const assetDetailCodedLookupSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});

const assetDetailKbTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const assetDetailComponentContextSchema = z.object({
  id: z.string(),
  assetType: assetDetailCodedLookupSchema,
  subsystem: assetDetailCodedLookupSchema,
  componentClass: assetDetailCodedLookupSchema,
  componentType: assetDetailKbTypeSchema,
});

const assetDetailComponentSchema = z.object({
  id: z.string(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  assetContextId: z.string().optional(),
  assetContext: assetDetailComponentContextSchema
    .nullable()
    .optional()
    .default(null),
  componentType: z.string().nullable().optional().default(null),
  connectorId: z.number().int().nullable().optional().default(null),
  ocppId: z.string().nullable().optional().default(null),
  controllerId: z.string().nullable().optional().default(null),
  controller: assetDetailComponentControllerSchema
    .nullable()
    .optional()
    .default(null),
  componentClass: assetLookupSchema.nullable(),
  parentComponent: assetLookupSchema.nullable(),
  installedAt: z.string().nullable(),
  removedAt: z.string().nullable(),
  attributes: jsonRecordSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

const assetDetailSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  assetName: z.string(),
  serialNumber: z.string().nullable().optional(),
  tenantAssetId: z.string().nullable().optional(),
  standard: z.enum([EvseType.AC, EvseType.DC, EvseType.AC_DC]).nullable(),
  manufacturer: z.string().nullable(),
  manufacturerId: z.string().nullable().optional(),
  installedAt: z.string().nullable(),
  commissionedAt: z.string().nullable(),
  decommissionedAt: z.string().nullable(),
  assetType: assetLookupSchema,
  assetModel: assetLookupSchema.nullable(),
  parentAsset: assetLookupSchema.nullable(),
  site: assetDetailSiteSchema,
  zone: assetDetailZoneSchema.nullable(),
  rawData: jsonRecordSchema,
  expectedDailyEarning: z.number(),
  landlordOrganizationId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  components: z.array(assetDetailComponentSchema),
  chargerAuthenticationConfigured: z.boolean().optional().default(false),
  // Charger liveness (ADR-0020): same contract as list assets / get-asset-details DTO.
  ...assetLivenessFieldsSchema,
  organizationExternalCsmsEnabled: z.boolean().optional().default(false),
  organizationChargerAuthenticationEnabled: z
    .boolean()
    .optional()
    .default(false),
});

export type AssetOcppSettings = GetAssetOcppSettingsResponseDto;
export type AssetOcppConfiguration = GetAssetOcppConfigurationResponseDto;

/** Discriminated request shapes aligned with OpenAPI ExecuteAssetOcppCommandRequestDto. */
export type AssetOcppCommandRequest =
  | { action: 'Reset'; type: OcppResetType }
  | { action: 'ClearCache' }
  | { action: 'GetLocalListVersion' }
  | { action: 'GetConfiguration'; keys: OcppGetConfigurationKey[] }
  | { action: 'TriggerMessage'; requestedMessage: OcppTriggerMessage }
  | {
      action: 'ChangeConfiguration';
      key: OcppChangeConfigurationKey;
      value: string;
    }
  | {
      action: 'GetDiagnostics';
      retries?: number | null;
      retryInterval?: number | null;
      startTime?: string | null;
      stopTime?: string | null;
    };

export type AssetOcppCommandResult = {
  action: ExecuteAssetOcppCommandResponseDto['action'] | 'GetDiagnostics';
  type?: ExecuteAssetOcppCommandResponseDto['type'];
  outcome: ExecuteAssetOcppCommandResponseDto['outcome'];
  uniqueId: string;
  requestPayload: Record<string, unknown>;
  responsePayload?: Record<string, unknown>;
  listVersion?: number;
  errorCode?: string;
  errorDescription?: string;
  diagnosticId?: string;
};

export class AssetOcppCommandError extends Error {
  readonly status: number;
  readonly reason?: string;

  constructor(status: number, reason: string | undefined, message: string) {
    super(message);
    this.name = 'AssetOcppCommandError';
    this.status = status;
    this.reason = reason;
  }
}

type ApiErrorPayload = {
  message?: string | string[];
};

function messageFromApiErrorPayload(
  payload: ApiErrorPayload | null,
  fallbackMessage: string,
): string {
  const rawMessage = payload?.message;
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter(Boolean).join('; ')
    : rawMessage;
  return message || fallbackMessage;
}

async function throwAssetApiError(
  error: unknown,
  fallbackMessage: string,
): Promise<never> {
  if (error instanceof ResponseError) {
    const payload = (await error.response
      .json()
      .catch(() => null)) as ApiErrorPayload | null;
    throw new Error(messageFromApiErrorPayload(payload, fallbackMessage));
  }

  throw error instanceof Error ? error : new Error(fallbackMessage);
}

async function throwAssetListApiError(
  error: unknown,
  fallbackMessage: string,
): Promise<never> {
  if (error instanceof ResponseError) {
    const payload = (await error.response
      .json()
      .catch(() => null)) as ApiErrorPayload | null;
    throw new AssetListHttpError(
      error.response.status,
      messageFromApiErrorPayload(payload, fallbackMessage),
    );
  }

  throw error instanceof Error ? error : new Error(fallbackMessage);
}

export type AssetLookup = z.infer<typeof assetLookupSchema>;
export type AssetListItem = z.infer<typeof assetListItemSchema>;
export type AssetPaginationMeta = z.infer<typeof assetPaginationMetaSchema>;
export type ListAssetsResponse = z.infer<typeof listAssetsResponseSchema>;
export type AssetDetailCity = z.infer<typeof assetDetailCitySchema>;
export type AssetDetailSite = z.infer<typeof assetDetailSiteSchema>;
export type AssetDetailZone = z.infer<typeof assetDetailZoneSchema>;
export type AssetDetailIncidentAffectedComponent = z.infer<
  typeof assetDetailIncidentAffectedComponentSchema
>;
export type AssetDetailIncident = z.infer<typeof assetDetailIncidentSchema>;
export type AssetDetailComponent = z.infer<typeof assetDetailComponentSchema>;
export type AssetDetail = z.infer<typeof assetDetailSchema>;

const assetSummarySchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  assetName: z.string(),
  serialNumber: z.string(),
  assetType: assetLookupSchema,
  assetModel: assetLookupSchema.nullable(),
  // Charger liveness (ADR-0020): same contract as list / details.
  ...assetLivenessFieldsSchema,
  site: z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
  }),
});

const listAssetIncidentsMetaSchema = assetPaginationMetaSchema.extend({
  openCount: z.number().int(),
  highPriorityCount: z.number().int(),
  latestCreatedAt: z.string().nullable(),
});

const listAssetIncidentsResponseSchema = z.object({
  data: z.array(assetDetailIncidentSchema),
  meta: listAssetIncidentsMetaSchema,
});

export type AssetSummary = z.infer<typeof assetSummarySchema>;
export type ListAssetIncidentsResponse = z.infer<
  typeof listAssetIncidentsResponseSchema
>;
export type UpdateAssetPayload = {
  assetName?: string;
  serialNumber?: string;
  manufacturerId?: string | null;
  siteId?: string;
  zoneId?: string | null;
  assetTypeId?: string;
  assetModelId?: string | null;
  parentAssetId?: string | null;
  installedAt?: string | null;
  commissionedAt?: string | null;
  decommissionedAt?: string | null;
  expectedDailyEarning?: number;
  landlordOrganizationId?: string | null;
  standard?: EvseType | null;
};

export type CreateAssetPayload = {
  assetName: string;
  serialNumber: string;
  manufacturer?: string | null;
  manufacturerId?: string | null;
  siteId: string;
  zoneId?: string | null;
  assetTypeId: string;
  assetModelId?: string | null;
  parentAssetId?: string | null;
  installedAt?: string | null;
  commissionedAt?: string | null;
  decommissionedAt?: string | null;
  expectedDailyEarning?: number;
  landlordOrganizationId?: string | null;
  standard?: EvseType | null;
};

export type ListAssetsParams = {
  organizationId: string;
  page: number;
  limit: number;
  q?: string;
  assetTypeId?: string;
  assetModelId?: string;
};

export const AssetService = {
  async create(
    organizationId: string,
    payload: CreateAssetPayload,
  ): Promise<AssetDetail> {
    const normalizedOrganizationId = organizationId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerCreateAssetRaw(
          {
            organizationId: normalizedOrganizationId,
            createAssetRequestDto: payload as CreateAssetRequestDto,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, assetDetailSchema);
    } catch (error) {
      return throwAssetApiError(error, 'Failed to create asset');
    }
  },

  async findDetailById(
    organizationId: string,
    assetId: string,
  ): Promise<AssetDetail> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerGetAssetDetailsByIdRaw(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, assetDetailSchema);
    } catch (error) {
      return throwAssetApiError(error, 'Failed to fetch asset details');
    }
  },

  async findSummaryById(
    organizationId: string,
    assetId: string,
  ): Promise<AssetSummary> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerGetAssetSummaryByIdRaw(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, assetSummarySchema);
    } catch (error) {
      return throwAssetApiError(error, 'Failed to fetch asset summary');
    }
  },

  async listIncidentsByAssetId(
    organizationId: string,
    assetId: string,
    params: { page: number; limit: number },
  ): Promise<ListAssetIncidentsResponse> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerListAssetIncidentsRaw(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            page: params.page,
            limit: params.limit,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(
        apiResponse,
        listAssetIncidentsResponseSchema,
      );
    } catch (error) {
      return throwAssetApiError(error, 'Failed to fetch asset incidents');
    }
  },

  async getOcppSettings(
    organizationId: string,
    assetId: string,
  ): Promise<AssetOcppSettings> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      return await assetApi.organizationAssetControllerGetAssetOcppSettings(
        {
          organizationId: normalizedOrganizationId,
          assetId: normalizedAssetId,
        },
        assetRequestInit,
      );
    } catch (error) {
      return throwAssetApiError(error, 'Failed to fetch asset OCPP settings');
    }
  },

  async getOcppConfiguration(
    organizationId: string,
    assetId: string,
  ): Promise<AssetOcppConfiguration> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      return (await assetApi.organizationAssetControllerGetAssetOcppConfiguration(
        {
          organizationId: normalizedOrganizationId,
          assetId: normalizedAssetId,
        },
        assetRequestInit,
      )) as AssetOcppConfiguration;
    } catch (error) {
      return throwAssetApiError(
        error,
        'Failed to fetch asset OCPP configuration',
      );
    }
  },

  async refreshOcppConfiguration(
    organizationId: string,
    assetId: string,
  ): Promise<AssetOcppConfiguration> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      return (await assetApi.organizationAssetControllerRefreshAssetOcppConfiguration(
        {
          organizationId: normalizedOrganizationId,
          assetId: normalizedAssetId,
        },
        assetRequestInit,
      )) as AssetOcppConfiguration;
    } catch (error) {
      return throwAssetApiError(
        error,
        'Failed to refresh asset OCPP configuration',
      );
    }
  },

  async executeOcppCommand(
    organizationId: string,
    assetId: string,
    body: AssetOcppCommandRequest,
  ): Promise<AssetOcppCommandResult> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    const requestDto: ExecuteAssetOcppCommandRequestDto =
      body.action === 'Reset'
        ? { action: 'Reset', type: body.type }
        : body.action === 'GetConfiguration'
          ? { action: 'GetConfiguration', keys: body.keys }
          : body.action === 'TriggerMessage'
            ? {
                action: 'TriggerMessage',
                requestedMessage: body.requestedMessage,
              }
            : body.action === 'ChangeConfiguration'
              ? {
                  action: 'ChangeConfiguration',
                  key: body.key,
                  value: body.value,
                }
              : body.action === 'GetDiagnostics'
                ? ({
                    action: 'GetDiagnostics',
                    ...(body.retries != null ? { retries: body.retries } : {}),
                    ...(body.retryInterval != null
                      ? { retryInterval: body.retryInterval }
                      : {}),
                    ...(body.startTime != null
                      ? { startTime: body.startTime }
                      : {}),
                    ...(body.stopTime != null
                      ? { stopTime: body.stopTime }
                      : {}),
                  } as unknown as ExecuteAssetOcppCommandRequestDto)
                : { action: body.action };

    try {
      const result =
        await assetApi.organizationAssetControllerExecuteOcppCommand(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            executeAssetOcppCommandRequestDto: requestDto,
          },
          assetRequestInit,
        );
      const diagnosticId =
        result != null &&
        typeof result === 'object' &&
        'diagnosticId' in result &&
        typeof (result as { diagnosticId?: unknown }).diagnosticId === 'string'
          ? (result as { diagnosticId: string }).diagnosticId
          : undefined;
      return {
        action: result.action,
        type: result.type,
        outcome: result.outcome,
        uniqueId: result.uniqueId,
        requestPayload: asRecord(result.requestPayload),
        responsePayload:
          result.responsePayload == null
            ? undefined
            : asRecord(result.responsePayload),
        listVersion: result.listVersion,
        errorCode: result.errorCode,
        errorDescription: result.errorDescription,
        ...(diagnosticId != null ? { diagnosticId } : {}),
      };
    } catch (error) {
      if (error instanceof ResponseError) {
        const payload = (await error.response.json().catch(() => null)) as {
          reason?: string;
          message?: string | { reason?: string };
        } | null;
        const nestedReason =
          payload?.message != null &&
          typeof payload.message === 'object' &&
          typeof payload.message.reason === 'string'
            ? payload.message.reason
            : undefined;
        const reason =
          typeof payload?.reason === 'string' ? payload.reason : nestedReason;
        const message =
          typeof payload?.message === 'string' && payload.message.trim()
            ? payload.message
            : 'The command could not be completed.';
        throw new AssetOcppCommandError(error.response.status, reason, message);
      }
      throw error instanceof Error
        ? error
        : new Error('The command could not be completed.');
    }
  },

  async deleteById(organizationId: string, assetId: string): Promise<void> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      await assetApi.organizationAssetControllerDeleteAssetById(
        {
          organizationId: normalizedOrganizationId,
          assetId: normalizedAssetId,
        },
        assetRequestInit,
      );
    } catch (error) {
      return throwAssetApiError(error, 'Failed to delete asset');
    }
  },

  async listByOrganization(
    params: ListAssetsParams,
  ): Promise<ListAssetsResponse> {
    const normalizedOrganizationId = params.organizationId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerListAssetsByOrganizationRaw(
          {
            organizationId: normalizedOrganizationId,
            page: params.page,
            limit: params.limit,
            ...(params.q?.trim() ? { q: params.q.trim() } : {}),
            ...(params.assetTypeId?.trim()
              ? { assetTypeId: params.assetTypeId.trim() }
              : {}),
            ...(params.assetModelId?.trim()
              ? { assetModelId: params.assetModelId.trim() }
              : {}),
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, listAssetsResponseSchema);
    } catch (error) {
      return throwAssetListApiError(error, 'Failed to fetch assets');
    }
  },

  async updateById(
    organizationId: string,
    assetId: string,
    payload: UpdateAssetPayload,
  ): Promise<AssetDetail> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerUpdateAssetByIdRaw(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            updateAssetRequestDto: payload as UpdateAssetRequestDto,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, assetDetailSchema);
    } catch (error) {
      return throwAssetApiError(error, 'Failed to update asset');
    }
  },

  async setOcppCredentials(
    organizationId: string,
    assetId: string,
    password: string,
  ): Promise<AssetDetail> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedAssetId = assetId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedAssetId) {
      throw new Error('Asset id is required');
    }

    const credentials: SetAssetOcppCredentialsRequestDto = { password };

    try {
      const apiResponse =
        await assetApi.organizationAssetControllerSetAssetOcppCredentialsRaw(
          {
            organizationId: normalizedOrganizationId,
            assetId: normalizedAssetId,
            setAssetOcppCredentialsRequestDto: credentials,
          },
          assetRequestInit,
        );
      return parseClientJsonResponse(apiResponse, assetDetailSchema);
    } catch (error) {
      return throwAssetApiError(error, 'Failed to save OCPP password');
    }
  },

  async findAllByOrganizationId(
    organizationId: string,
  ): Promise<AssetLookup[]> {
    const assets = await this.listByOrganization({
      organizationId,
      page: 1,
      limit: 1000,
    });

    return assets.data.map((asset) => ({
      id: asset.id,
      name: asset.assetName,
    }));
  },
  async findAssetModelsByAssetTypeId(
    assetTypeId: string,
  ): Promise<{ id: string; name: string }[]> {
    try {
      const result =
        await assetApi.assetControllerGetAssetModelsMetadataByAssetTypeId(
          { assetTypeId },
          assetRequestInit,
        );
      return result.map((model) => ({
        id: model.id,
        name: model.name,
      }));
    } catch (error) {
      return throwAssetApiError(error, 'Failed to fetch asset models');
    }
  },
};
